import AppInfoFooter from "@/components/AppInfoFooter";
import Button from "@/components/Button";
import HalfSpace from "@/components/HalfSpace";
import { EmailInput, PasswordInput } from "@/components/InputFields";
import Link from "@/components/Link";
import Page from "@/components/Page";
import {
  useCheckSignUpInvitationQuery,
  useResendInvitationEmailMutation,
  useSignUpMutation,
} from "@/data/queries";
import { backendErrorCodeFor } from "@/model/BackendErrors";
import { passwordRegex } from "@/model/Constants";
import { VerifyCodePageStateParams } from "@/screens/auth/verify-code/VerifyCodePage";
import cn from "@/util/cn";
import { useRawSearchParams } from "@/util/hooks/useSearchParams";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

type SignUpPageSearchParams = {
  token: string;
  entityId: string;
  username: string;
};

type SignUpFormData = {
  password: string;
  passwordConfirmation: string;
};

const SignUpPage = () => {
  // The BioT signup query string parameters are not URL encoded, so we use the hook that parses them raw
  const { token, entityId: userId, username: email } = useRawSearchParams<SignUpPageSearchParams>();
  const navigate = useNavigate();
  const {
    register,
    watch,
    trigger: validate,
    handleSubmit,
    formState,
  } = useForm<SignUpFormData>({ mode: "onBlur" });

  const { isPending: isValidatingInvitation, error: invitationValidationError } =
    useCheckSignUpInvitationQuery({ token, userId });

  const {
    mutateAsync: resendInvitation,
    isPending: isResendingInvitation,
    error: resendInvitationError,
  } = useResendInvitationEmailMutation();

  const { mutateAsync: signUp, isPending: isSigningUp, error: signUpError } = useSignUpMutation();

  const { errors, touchedFields } = formState;
  const password = watch("password");
  const hasValidInvitation = !isValidatingInvitation && invitationValidationError === null;
  const isInvitationExpired =
    backendErrorCodeFor(invitationValidationError) === "SIGN_UP_INVITATION_EXPIRED";
  const errorCode = backendErrorCodeFor(
    resendInvitationError ?? invitationValidationError ?? signUpError
  );

  const validatePassword = (password: string) => passwordRegex.test(password);

  const validatePasswordConfirmation = (passwordConfirmation: string) =>
    passwordConfirmation === password || "Passwords do not match";

  const onSubmit = async (formData: SignUpFormData) => {
    const result = await signUp({ token, userId, password: formData.password });
    if (result.type === "mfa-challenge") {
      const state: VerifyCodePageStateParams = { challenge: result.challenge, email };
      navigate("/verify-code", { state });
      return;
    }
    navigate("/", { replace: true });
  };

  const onResendInvitationSelected = async () => {
    await resendInvitation({ email });
    navigate("/resend-invitation-success", { replace: true });
  };

  return (
    <Page className="bg-gray-50">
      <div className="flex h-full flex-col justify-between">
        <div
          className={cn("mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl")}
        >
          <h2 className="mb-4 text-center text-4xl text-primary/90">
            Welcome to
            <br />
            Bloomlife Connects
          </h2>
          {!isInvitationExpired && (
            <h3 className="mb-8 text-center text-lg text-primary/90">
              Please create your password below
            </h3>
          )}
          {errorCode && (
            <div className="mb-4 mt-8 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
              <span className="material-icons me-4 text-red-600">error</span>
              <p className="text-red-600">
                {errorCode === "SIGN_UP_INVITATION_EXPIRED" ? (
                  "This invitation has expired."
                ) : errorCode === "SIGN_UP_INVITATION_ALREADY_ACCEPTED" ? (
                  <>
                    This invitation has already been accepted.
                    <br />
                    Please&nbsp;
                    <Link to="/login" state={{ email }}>
                      log in
                    </Link>
                    .
                  </>
                ) : errorCode === "WEAK_PASSWORD" ? (
                  "The password is commonly used and easily guessed. Please choose a stronger password"
                ) : errorCode === "MFA_SETUP_REQUIRED" ? (
                  "Your account is not set up for two-step verification. Please contact Bloomlife Support."
                ) : (
                  "Something went wrong. Please contact Bloomlife."
                )}
              </p>
            </div>
          )}
          {isInvitationExpired ? (
            <Button
              variant="primary"
              type="button"
              disabled={isResendingInvitation}
              className="mt-8 w-full text-base"
              onClick={onResendInvitationSelected}
            >
              Resend invitation
            </Button>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmailInput autoComplete="username" disabled value={email} className="hidden" />
              <label htmlFor="password" className="block leading-6 text-primary/90">
                Password
              </label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                className="mt-2 disabled:cursor-not-allowed"
                disabled={isSigningUp || !hasValidInvitation}
                {...register("password", {
                  validate: validatePassword,
                  onChange: () => {
                    if (touchedFields.password) {
                      validate("password");
                    }
                  },
                })}
              />
              <p
                className={cn(
                  "mt-2 text-sm italic",
                  errors?.password ? "text-red-600" : "text-primary"
                )}
              >
                Password must have at least 8&nbsp;characters that include at least 1&nbsp;lowercase
                character, 1&nbsp;uppercase character, 1&nbsp;number and one of these special
                characters:
                <span className="inline-block">
                  $<HalfSpace />@<HalfSpace />%<HalfSpace />*<HalfSpace />#<HalfSpace />?
                  <HalfSpace />!<HalfSpace />-<HalfSpace />_
                </span>
              </p>
              <label
                htmlFor="password-confirmation"
                className="mt-4 block leading-6 text-primary/90"
              >
                Confirm password
              </label>
              <PasswordInput
                id="password-confirmation"
                autoComplete="new-password"
                className="mt-2 disabled:cursor-not-allowed"
                disabled={isSigningUp || !hasValidInvitation}
                {...register("passwordConfirmation", {
                  validate: validatePasswordConfirmation,
                  onChange: () => {
                    if (touchedFields.passwordConfirmation) {
                      validate("passwordConfirmation");
                    }
                  },
                })}
              />
              {errors?.passwordConfirmation && (
                <p className="mt-2 text-sm italic text-red-600">
                  {errors.passwordConfirmation.message}
                </p>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={isSigningUp || !hasValidInvitation}
                className="mt-8 w-full text-base"
              >
                Sign up
              </Button>
            </form>
          )}
        </div>
        <AppInfoFooter className="mt-auto p-4" />
      </div>
    </Page>
  );
};

export default SignUpPage;
